import type * as Immutable from 'immutable'
import {
  GroupModel as FactoryGroupModel,
  MetricItemModel as FactoryMetricItemModel
} from '@aytm/app/insights/dashboard/models/BlockModel.models'
import { InsightsDashboardPoolState } from '@aytm/app/insights/dashboard/duckness/ducks/types'
import type {
  AttributeRow,
  AttributeCaption,
  SectionType,
  TargetType,
  Metrics,
  MetricsType,
  AttributeType,
  SectionAttribute,
  ItemType
} from '@aytm/app/insights/dashboard/models/types.ClientDTO'
import type {
  MetricsModel,
  MetricsListModel,
  GroupModel,
  GroupListModel,
  GroupItemsListModel
} from '@aytm/app/insights/dashboard/models/types.ClientModel'
import { getCurrentBlock, updateCurrentBlock } from '@aytm/app/insights/dashboard/helpers/requests'
import { processItems, updateHiddenItems } from '@aytm/app/insights/dashboard/helpers/items'
import type { Factory } from '@aytm/app/insights/dashboard/helperTypes'

import staticStore from '@aytm/app/insights/dashboard/stores/static.store'
import type { BlockModelShape } from '@aytm/app/insights/dashboard/models/BlockModel'
import { isIdentityVector } from '@aytm/app/insights/dashboard/lib/arrays'
import type ItemListModel from '@aytm/app/survey/editor/models/ItemListModel'

export type AttributeSortPayload = {
  attribute: string
  method: string | null
  target: string
}

export type AttributeDeletePayload = {
  type: string
  section: string
  target: string
  attribute: string
  method: string
}

export type AttributeAddPayload = {
  section: string
  target: string
  attribute?: string
  referenceSection?: string
}

export type AttributeReplacePayload = {
  from: SectionAttribute
  to: SectionAttribute
}

export type AttributeMetricsChangePayload = Record<
  string,
  {
    section: string
    target: string
    method?: string
    values?: string[]
    show?: boolean
  }
>

export type SectionAddPayload = {
  section: string
  target: string
  referenceSection?: string
}

export type SectionRenamePayload = {
  section: string
  name: string
}

export type SectionDeletePayload = {
  section: string
  target: string
}

export type SectionReplacePayload = {
  section: string
  dropped: string
}

export type ShowSectionChangePayload = {
  section: string
  target: string
  show: boolean
}

export type MetricsFactoryParams = {
  attributes: Record<string, AttributeType>
  dataMetrics: Record<string, Metrics[]>
  sections: GroupListModel
  trackTarget?: string
}

export type GetCaptionsOptions = {
  showSections?: boolean
  showMetrics?: boolean
  showMetadata?: boolean
  showCollapsed?: boolean
  showUnique?: boolean
}

export type GetMetrics = (attribute: string, section?: string, attributeMethod?: string) => Metrics

export type GetCaptions = (options?: Partial<GetCaptionsOptions>) => AttributeCaption[]

type AddSectionParams = {
  sections: GroupListModel
  section: string
  target: string
  referenceSection?: string
}

type AddAttributeToSectionParams = {
  sections: GroupListModel
  attribute?: string
  section: string
  target: string
  metrics?: Metrics
  referenceSection?: string
}

type AddAttributesToSectionParams = {
  sections: GroupListModel
  attributes: string[]
  section: string
  target: string
  metricsArray: Metrics[]
}

type MoveAttributeParams = {
  sections: GroupListModel
  from: SectionAttribute
  to: SectionAttribute
  after?: boolean
}

export const metricsFactory =
  ({ attributes, dataMetrics, sections }: MetricsFactoryParams) =>
  (attribute: string, section?: string, attributeMethod?: string): Metrics => {
    if (staticStore.featureMultiMetrics) {
      const sectionIndex = sections?.findIndex(
        section
          ? (s: GroupModel) => section === s.get('name')
          : (s: GroupModel) => Boolean(s.get('items')?.includes?.(attribute))
      )
      if (sectionIndex >= 0) {
        const metricsList = sections?.getIn<MetricsListModel>([sectionIndex, 'metrics'])
        const metricsItem = metricsList?.find((v: MetricsModel) => v.get('uid') === attribute)
        const method = metricsItem?.get('method')
        const key = attributes?.[attribute]?.key || attribute
        const metricsOptions = dataMetrics[key] || null
        const defaultMethod = attributeMethod || method || attributes?.[attribute]?.method || null
        return metricsOptions?.find(v => v.id === defaultMethod) || metricsOptions?.[0] || null
      }
    }
    return null
  }

const expandAttributeMetrics = ({ attributes, dataMetrics, sections, trackTarget }: MetricsFactoryParams) => {
  const getMetrics = metricsFactory({ attributes, dataMetrics, sections })
  return (attribute: string, sectionName: string, showMetrics: boolean = true): AttributeRow[] => {
    const acc = [] as AttributeRow[]
    const currentTarget = 'entity' === trackTarget ? 'attribute' : 'entity'
    // Read schema attribute metrics state
    const sectionIndex = sections?.findIndex(s => sectionName === s.get('name') && currentTarget === s.get('target'))
    if (sectionIndex >= 0) {
      const target = sections?.getIn<SectionType['target']>([sectionIndex, 'target']) || null
      const metricsList = sections?.getIn<MetricsListModel>([sectionIndex, 'metrics'])
      const metricsItem = metricsList?.find((v: MetricsModel) => v.get('uid') === attribute)
      const { values, disabled, show } = metricsItem?.toJS<MetricsType>() || {}
      // All attribute metrics available
      const key = attributes?.[attribute]?.key || attribute
      const metricsOptions = dataMetrics[key] || null
      // Selected metrics
      const selectedMetrics = metricsOptions?.filter(v => (values || []).includes(v.id)).map(({ id }) => id) || []
      // Default metric for main attribute
      const defaultMetrics = getMetrics(attribute, sectionName)
      // Caption name
      const name = attributes[attribute]?.text || attribute
      // Data for attribute row
      acc.push({
        type: 'attribute',
        target,
        attribute,
        section: sectionName,
        metrics: defaultMetrics,
        metricsOptions,
        defaultMetrics,
        showMetrics: show,
        selectedMetrics,
        disabled: disabled || [],
        name
      } as AttributeRow)
      // Create child rows for different metrics
      if (values && show && showMetrics) {
        const metricList = Array.isArray(metricsOptions) ? metricsOptions.filter(v => values.includes(v.id)) : []
        if (metricList.length && staticStore.featureMultiMetrics) {
          for (const metrics of metricList) {
            acc.push({
              type: 'metrics',
              target,
              attribute,
              section: sectionName,
              metrics,
              metricsOptions,
              defaultMetrics,
              selectedMetrics,
              disabled: disabled || [],
              showMetrics: false,
              name
            } as AttributeRow)
          }
        }
      }
    }
    // Done
    return acc
  }
}

function isAttributeRow(rowItem: AttributeCaption): rowItem is AttributeRow {
  return ['metrics', 'attribute'].includes(rowItem.type)
}

function addSection({ sections, section, target, referenceSection }: AddSectionParams): GroupListModel {
  const sectionIndex = sections.findIndex(v => section === v.get('name') && target === v.get('target'))
  if (sectionIndex >= 0) {
    return sections
  } else {
    const referenceSectionIndex = referenceSection
      ? sections.findIndex(v => referenceSection === v.get('name') && target === v.get('target'))
      : sections.size - 1
    const newSections = sections.splice(
      referenceSectionIndex + 1,
      0,
      (FactoryGroupModel as unknown as Factory).of({ name: section, target, items: [] } as const)
    )
    return newSections as unknown as GroupListModel
  }
}

function addAttributeToSection({
  sections,
  attribute,
  section,
  target,
  metrics,
  referenceSection
}: AddAttributeToSectionParams): GroupListModel {
  let newSections = addSection({ sections, section, target, referenceSection })
  if (!attribute) {
    return newSections
  }
  const sectionIndex = newSections.findIndex(v => section === v.get('name') && target === v.get('target'))
  newSections = newSections
    .updateIn([sectionIndex, 'items'], (items: string[]) => (items.includes(attribute) ? items : items.push(attribute)))
    .setIn(
      [sectionIndex, 'metrics', attribute],
      metrics
        ? (FactoryMetricItemModel as unknown as Factory).of({ uid: attribute, method: metrics.id, values: [] })
        : null
    )
    .updateIn(
      [sectionIndex],
      (section: Immutable.Map<string, unknown>): Immutable.Map<string, unknown> => section.set('collapsed', false)
    ) as unknown as GroupListModel
  return newSections
}

function addAttributesToSection({
  sections,
  attributes,
  section,
  target,
  metricsArray
}: AddAttributesToSectionParams): GroupListModel {
  return attributes.reduce(
    (sections, attribute, index) =>
      addAttributeToSection({ sections, attribute, section, target, metrics: metricsArray[index] }),
    sections.clone()
  )
}

function moveAttribute({ sections, from, to, after }: MoveAttributeParams): GroupListModel {
  let sectionsResult = sections

  if (from.target !== to.target) {
    return sectionsResult
  }

  // Movement is allowed only inside the same section target
  const fromSectionIndex = sectionsResult.findIndex(
    section => from.name === section.get('name') && section.get('target') === from.target
  )
  const toSectionIndex = sectionsResult.findIndex(
    section => to.name === section.get('name') && section.get('target') === to.target
  )

  if (fromSectionIndex < 0 || toSectionIndex < 0) {
    return sectionsResult
  }

  const fromAttributeIndex: number =
    sectionsResult.getIn<GroupItemsListModel>([fromSectionIndex, 'items'])?.indexOf?.(from.attribute) ?? -1

  const toAttributeIndex: number = to.attribute
    ? (sectionsResult.getIn<GroupItemsListModel>([toSectionIndex, 'items'])?.indexOf(to.attribute) ?? -1)
    : -1

  if (fromAttributeIndex < 0) {
    return sectionsResult
    // return addAttribute(sectionsResult, to.attribute, to.name, to.target)
  }

  if (fromSectionIndex === toSectionIndex) {
    // Same section
    if (fromAttributeIndex >= 0 && toAttributeIndex >= 0 && fromAttributeIndex !== toAttributeIndex) {
      sectionsResult = sectionsResult.updateIn<GroupItemsListModel>([fromSectionIndex, 'items'], items =>
        items.set(fromAttributeIndex, to.attribute)
      )
      sectionsResult = sectionsResult.updateIn<GroupItemsListModel>([toSectionIndex, 'items'], items =>
        items.set(toAttributeIndex, from.attribute)
      )
    }
  } else {
    const sameNameAttributeIndex =
      sectionsResult.getIn<GroupItemsListModel>([toSectionIndex, 'items'])?.indexOf(from.attribute) ?? -1
    if (sameNameAttributeIndex >= 0) {
      return sectionsResult
    }
    const isMovingDown = fromSectionIndex < toSectionIndex
    // Sections differ
    if (fromAttributeIndex >= 0) {
      sectionsResult = sectionsResult.updateIn<GroupItemsListModel>([fromSectionIndex, 'items'], items =>
        items.delete(fromAttributeIndex)
      )
    }
    sectionsResult =
      toAttributeIndex >= 0
        ? sectionsResult
            .updateIn<GroupItemsListModel>([toSectionIndex, 'items'], items =>
              items.insert(toAttributeIndex + (after ? 1 : 0), from.attribute)
            )
            .setIn<boolean>([toSectionIndex, 'collapsed'], false)
        : sectionsResult
            .updateIn<GroupItemsListModel>([toSectionIndex, 'items'], items =>
              items[isMovingDown ? 'unshift' : 'push'](from.attribute)
            )
            .setIn<boolean>([toSectionIndex, 'collapsed'], false)
  }

  return sectionsResult
}

type DeleteSectionParams = {
  sections: GroupListModel
  section: string
  target: string
}

function deleteSection({ sections, section, target }: DeleteSectionParams): GroupListModel {
  const sectionIndex = sections.findIndex(v => section === v.get('name') && target === v.get('target'))
  return 0 <= sectionIndex ? (sections.delete(sectionIndex) as unknown as GroupListModel) : sections
}

type DeleteAttributeParams = {
  sections: GroupListModel
  section: string
  target: string
  attribute?: string
}

function deleteAttribute({ sections, attribute, section, target }: DeleteAttributeParams): GroupListModel {
  const sectionIndex = sections.findIndex(v => section === v.get('name') && target === v.get('target'))
  if (attribute) {
    const metricsIndex = sections
      .getIn<MetricsListModel>([sectionIndex, 'metrics'])
      .findIndex(metric => metric.get('uid') === attribute)
    return sectionIndex >= 0
      ? sections
          .updateIn<GroupItemsListModel>([sectionIndex, 'items'], items => {
            const index = items.indexOf(attribute)
            return index >= 0 ? items.delete(index) : items
          })
          .updateIn<GroupItemsListModel>([sectionIndex, 'metrics'], metrics =>
            metricsIndex >= 0 ? metrics.delete(metricsIndex) : metrics
          )
      : sections
  } else {
    return deleteSection({ sections, section, target })
  }
}

export const captionsFactory = ({ attributes, dataMetrics, sections, trackTarget }: MetricsFactoryParams) => {
  const expandFunction = expandAttributeMetrics({ attributes, dataMetrics, sections, trackTarget })
  return ({
    showSections = true,
    showMetrics = true,
    showMetadata = true,
    showCollapsed = false,
    showUnique = false
  }: Partial<GetCaptionsOptions> = {}): AttributeCaption[] => {
    const result: AttributeCaption[] =
      sections
        ?.filter(section => trackTarget !== section.get('target'))
        .reduce((acc, section) => {
          // Section caption
          if (showSections) {
            acc.push({
              type: 'section',
              section: section.get('name'),
              target: section.get('target'),
              collapsed: section.get('collapsed')
            })
          }
          if (!section.get('collapsed') || showCollapsed) {
            if ('meta' === section.get('target')) {
              if (showMetadata) {
                // Metadata captions
                for (const attributeKey of section.get('items')?.toJS?.() || []) {
                  acc.push({
                    type: 'attribute',
                    attribute: attributeKey,
                    target: 'meta' as TargetType,
                    section: section.get('name'),
                    name: attributes[attributeKey]?.text || attributeKey
                  })
                }
              }
            } else {
              // Attribute captions
              for (const attributeKey of section.get('items')?.toJS?.() || []) {
                if (attributes[attributeKey]) {
                  acc.push(...expandFunction(attributeKey, section.get('name'), showMetrics))
                }
              }
            }
          }
          // Done
          return acc
        }, [] as AttributeCaption[]) || ([] as AttributeCaption[])

    // Add other types of captions if needed
    const finalResult = showUnique
      ? result.reduce((accumulated, current) => {
          if (isAttributeRow(current)) {
            const isDuplicate = (accumulated as AttributeRow[]).some(
              r =>
                current.attribute === r.attribute &&
                (!current.metrics || !r.metrics || current.metrics.id === r.metrics.id)
            )
            if (isDuplicate) {
              return accumulated
            }
          }
          accumulated.push(current)
          return accumulated
        }, [] as AttributeCaption[])
      : result
    return finalResult
  }
}

export const getCurrentMethod = (state: InsightsDashboardPoolState): string | null => {
  const block = getCurrentBlock(state)
  const sortByMethod = staticStore.featureMultiMetrics ? block?.get<string>('sortByMethod') || null : null
  if (!sortByMethod) {
    const attributes = state.currentData?.attributes || {}
    const dataMetrics = state.currentData?.dataMetrics || {}
    const sections = block?.getSections()
    const sortByAttribute = block?.get<string>('sortByAttribute')
    const metrics = metricsFactory({ attributes, dataMetrics, sections })(sortByAttribute)
    return metrics?.id || null
  } else {
    return sortByMethod
  }
}

export const getCaptions = (
  state: InsightsDashboardPoolState,
  {
    showSections = true,
    showMetrics = true,
    showMetadata = true,
    showCollapsed = false,
    showUnique = false
  }: Partial<GetCaptionsOptions> = {}
): AttributeCaption[] => {
  const block = getCurrentBlock(state)
  const attributes = state.currentData?.attributes || {}
  const dataMetrics = state.currentData?.dataMetrics || {}
  const sections = block?.getSections()
  const trackTarget = block?.getObject('trackTarget') || 'entity'
  return captionsFactory({ attributes, dataMetrics, sections, trackTarget })({
    showSections,
    showMetrics,
    showMetadata,
    showCollapsed,
    showUnique
  })
}

/**
 * Calculates new state when user clicks on attribute
 */
export const attributeSort = (
  state: InsightsDashboardPoolState,
  { attribute, method, target }: AttributeSortPayload
): InsightsDashboardPoolState => {
  const block = getCurrentBlock(state)
  const subjectType = block.getSubjectType()
  // debugger
  if ('wave' === subjectType && 'meta' === target) {
    // In tracker mode ignore clocking on meta attributes
    return state
  }

  let resultState = state

  const store = state.currentData
  const presets = store.presets

  const blockItems: ItemType[] = (block.get<ItemListModel>('items')?.toJS() as ItemType[]) || []
  const currentPreset = block.get<string>('currentPreset') || 'All'
  const sortByAttribute = block.get<string>('sortByAttribute') || null
  const sortByMethod = block.get<string>('sortByMethod') || null
  const sortOrder = block.get<BlockModelShape['sortOrder']>('sortOrder') || null

  const canChangeSelectedAttribute =
    'All' === currentPreset || presets?.[currentPreset]?.[attribute] || !presets?.[currentPreset]

  if ('none' === attribute) {
    // Invalidate sort order and clear all sort-related highlighting
    resultState = updateCurrentBlock(resultState, {
      sortOrder: 'letters',
      sortOrderManualEnabled: false,
      // sortOrderPositions: null // Keep current positions
      sortByAttribute: null,
      sortByMethod: null,
      chartAttribute: null,
      chartMethod: null
    })
  } else if ('--manual--' === attribute) {
    resultState = updateCurrentBlock(resultState, {
      sortOrder: 'manual',
      // sortByAttribute: null, // Keep current attribute (selected)
      // sortByMethod: null,
      sortOrderManualEnabled: true
    })
  } else if (attribute === sortByAttribute && method === sortByMethod) {
    // When click on the same attribute just change sort order
    if ('wave' === subjectType) {
      // In tracker mode just toggle sortBy/chart attribute
      resultState = updateCurrentBlock(resultState, {
        sortByAttribute: sortByAttribute ? null : attribute,
        sortOrderManualEnabled: false
      })
    } else {
      // In concept mode toggle sort order and chart attribute
      const newSortOrder = 'desc' === sortOrder ? 'asc' : 'desc'
      resultState = updateCurrentBlock(resultState, { sortOrder: newSortOrder, sortOrderManualEnabled: false })
    }
  } else {
    // Set new sort by attribute and selected attribute
    if (canChangeSelectedAttribute) {
      // Process preset attributes
      const nextItems = processItems(state, { preset: currentPreset, attribute })
      const nextItemsArray: ItemType[] = nextItems.toJS()
      const skipItems =
        blockItems.length === nextItemsArray.length &&
        blockItems.every((item, itemIndex) => item.uid === nextItemsArray[itemIndex]?.uid)
      // Items are taken from current preset
      // Update model with new items for items from preset
      // and fetch data for new items for defined preset
      resultState = updateCurrentBlock(
        resultState,
        Object.assign(
          {
            sortByAttribute: attribute,
            sortByMethod: method,
            sortOrder: ['asc', 'desc'].includes(sortOrder) ? sortOrder : 'desc',
            sortOrderManualEnabled: false,
            ...(target !== 'meta' ? { chartAttribute: attribute, chartMethod: method ?? null } : {})
          },
          !skipItems
            ? {
                items: nextItems,
                sortOrderPositions: null
              }
            : {}
        )
      )
    } else {
      // Update sort by attribute in block schema switching to 'custom' preset
      resultState = updateCurrentBlock(resultState, {
        sortByAttribute: attribute,
        sortByMethod: method,
        currentPreset: 'custom',
        hideBlankColumns: false,
        sortOrderManualEnabled: false,
        ...(target !== 'meta' ? { chartAttribute: attribute, chartMethod: method ?? null } : {})
      })
    }
  }

  return resultState
}

/**
 * Detects is data refresh needed when user clicks on attribute
 */
export const attributeSortRefresh = (
  state: InsightsDashboardPoolState,
  { attribute, method }: AttributeSortPayload
): boolean => {
  const block = getCurrentBlock(state)

  const subjectType = block.getSubjectType()
  const currentPreset = block.get<string>('currentPreset') || 'All'
  const sortByAttribute = block.get<string>('sortByAttribute') || null
  const sortByMethod = block.get<string>('sortByMethod') || null

  if ('wave' === subjectType) {
    return false
  } else if ('none' === attribute) {
    return false
  } else if (attribute === sortByAttribute && method === sortByMethod) {
    return false
  } else if (['All', 'custom'].includes(currentPreset)) {
    return false
  }

  return true
}

export const attributeDelete = (
  state: InsightsDashboardPoolState,
  { type, section, target, attribute, method }: AttributeDeletePayload
): InsightsDashboardPoolState => {
  let resultState = state

  const block = getCurrentBlock(state)
  const sections = block.getSections()
  const sortByAttribute = block.get<string>('sortByAttribute') || null
  const sortByMethod = block.get<string>('sortByMethod') || null

  if ('metrics' === type) {
    // find section
    const sectionIndex = sections.findIndex(v => section === v.get('name') && target === v.get('target'))
    if (sectionIndex >= 0) {
      // Get new sections
      const newSections = sections.updateIn([sectionIndex, 'metrics', attribute, 'values'], (values: string[]) =>
        values.filter((v: string) => v !== method)
      )
      // If current metrics is deleted then set sort by method to null
      resultState =
        method === sortByMethod
          ? updateCurrentBlock(resultState, { sections: newSections, sortByMethod: null })
          : updateCurrentBlock(resultState, { sections: newSections })
    }
  } else if ('attribute' === type) {
    // Get new sections
    const newSections = deleteAttribute({ sections, attribute, section, target })
    // If current attribute is deleted then set sort by attribute and method to null
    resultState =
      attribute === sortByAttribute
        ? updateCurrentBlock(resultState, {
            sections: newSections,
            sortByMethod: null,
            sortByAttribute: null
          })
        : updateCurrentBlock(resultState, {
            sections: newSections
          })
  }

  resultState = updateHiddenItems(resultState)

  return resultState
}

export const attributeAdd = (
  state: InsightsDashboardPoolState,
  { section, target, attribute, referenceSection }: AttributeAddPayload
): InsightsDashboardPoolState => {
  const block = getCurrentBlock(state)
  const dataMetrics = state.currentData?.dataMetrics || {}
  const attributes = state.currentData?.attributes || {}
  const sections = block?.getSections()
  const getMetrics = metricsFactory({ attributes, dataMetrics, sections })
  const metrics = getMetrics(attribute, section)
  const newSections = addAttributeToSection({ sections, attribute, section, target, metrics, referenceSection })
  let resultState = updateCurrentBlock(state, { sections: newSections })
  resultState = updateHiddenItems(resultState)
  return resultState
}

export const attributeReplace = (
  state: InsightsDashboardPoolState,
  { from, to }: AttributeReplacePayload
): InsightsDashboardPoolState => {
  const block = getCurrentBlock(state)
  const sections = block.getSections()
  const newSections = moveAttribute({ sections, from, to })
  return updateCurrentBlock(state, { sections: newSections })
}

export type ManualOrderUpdatePayload = { positions?: null | number[]; isManual?: boolean }

export const sortOrderPositionsUpdate = (
  state: InsightsDashboardPoolState,
  { positions, isManual }: ManualOrderUpdatePayload
): InsightsDashboardPoolState => {
  const isIdentity = isIdentityVector(positions)
  const updatePayload = Object.assign(
    {},
    // sortOrderPositions:
    // positions ? { sortOrderPositions: isIdentity ? null : positions } : {},
    // sortOrderManualEnabled:
    isManual !== undefined
      ? { sortOrderManualEnabled: isManual }
      : positions
        ? { sortOrderManualEnabled: !isIdentity }
        : {}
  )
  return updateCurrentBlock(state, updatePayload)
}

export const attributeMetricsChange = (
  state: InsightsDashboardPoolState,
  payload: AttributeMetricsChangePayload
): InsightsDashboardPoolState => {
  let resultState = state

  const block = getCurrentBlock(state)
  const sections = block.getSections()

  let newSections = sections
  for (const [attribute, object] of Object.entries(payload)) {
    const sectionIndex = sections.findIndex(v => object.section === v.get('name') && object.target === v.get('target'))
    if (sectionIndex >= 0) {
      if ('values' in object) {
        newSections = newSections.setIn<string[]>([sectionIndex, 'metrics', attribute, 'values'], object.values || [])
      }
      if ('method' in object) {
        newSections = newSections.setIn<string>([sectionIndex, 'metrics', attribute, 'method'], object.method || null)
      }
      if ('show' in object) {
        newSections = newSections.setIn<boolean>([sectionIndex, 'metrics', attribute, 'show'], Boolean(object.show))
      }
      resultState = updateCurrentBlock(state, { sections: newSections })
    }
  }

  return resultState
}

/**
 * Shows/Hides section
 */
export const showSectionChange = (
  state: InsightsDashboardPoolState,
  { section, target, show }: ShowSectionChangePayload
) => {
  let resultState = state
  const block = getCurrentBlock(state)
  const sections = block?.getSections()
  const sectionIndex = sections.findIndex(v => section === v.get('name') && target === v.get('target'))
  if (sectionIndex >= 0) {
    const newSections = sections.updateIn([sectionIndex, 'collapsed'], () => !show) as unknown as GroupListModel
    resultState = updateCurrentBlock(resultState, { sections: newSections })
  }
  return resultState
}

export const sectionAdd = (
  state: InsightsDashboardPoolState,
  { section, target, referenceSection }: SectionAddPayload
): InsightsDashboardPoolState => {
  const block = getCurrentBlock(state)
  const sections = block?.getSections()
  const newSections = addSection({ sections, section, target, referenceSection })
  return updateCurrentBlock(state, { sections: newSections })
}

export const sectionRename = (state: InsightsDashboardPoolState, { section, name }: SectionRenamePayload) => {
  const block = getCurrentBlock(state)
  const sections = block?.getSections()
  const sectionIndex = sections.findIndex(v => section === v.get('name'))
  if (sectionIndex >= 0) {
    const newSections = sections.setIn([sectionIndex, 'name'], name) as unknown as GroupListModel
    return updateCurrentBlock(state, { sections: newSections })
  }
  return state
}

export const sectionDelete = (state: InsightsDashboardPoolState, { section, target }: SectionDeletePayload) => {
  const block = getCurrentBlock(state)
  const sections = block?.getSections()
  const sectionIndex = sections.findIndex(v => section === v.get('name') && target === v.get('target'))
  if (sectionIndex >= 0) {
    const newSections = sections.delete(sectionIndex) as unknown as GroupListModel
    return updateCurrentBlock(state, { sections: newSections })
  }
  return state
}

export const sectionReplace = (state: InsightsDashboardPoolState, { section, dropped }: SectionReplacePayload) => {
  const block = getCurrentBlock(state)
  const sections = block?.getSections()

  const sectionIndex = sections.findIndex(v => v.name === section)
  const droppedIndex = sections.findIndex(v => v.name === dropped)

  if (-1 === sectionIndex || -1 === droppedIndex) {
    return state
  }

  let newSections = sections
  const droppedSection = sections.get(droppedIndex)

  newSections =
    droppedIndex < sectionIndex
      ? // Insert dropped section after the stationary section
        (newSections = newSections
          .splice(droppedIndex, 1)
          .splice(sectionIndex, 0, droppedSection) as unknown as GroupListModel)
      : // Insert dropped section before the stationary section
        (newSections = newSections
          .splice(droppedIndex, 1)
          .splice(sectionIndex, 0, droppedSection) as unknown as GroupListModel)

  return updateCurrentBlock(state, { sections: newSections })
}

export type SectionApplyMetricsPayload = {
  section: string
  target: TargetType
  method: string
}

export const sectionApplyMetrics = (
  state: InsightsDashboardPoolState,
  { section: sectionName, target, method }: SectionApplyMetricsPayload
) => {
  const block = getCurrentBlock(state)
  const sections = block?.getSections()
  const dataMetrics = state.currentData?.dataMetrics || {}

  const sectionIndex = sections.findIndex(v => sectionName === v.get('name') && target === v.get('target'))
  const section = sections.find(v => sectionName === v.get('name') && target === v.get('target'))

  if (-1 === sectionIndex) {
    return state
  }

  let newSections = sections

  const items = section.get('items')?.toJS?.() || []

  for (const uid of items) {
    const availableMetrics = dataMetrics[uid] || []
    if (!availableMetrics.some(v => v.id === method)) {
      continue
    }

    newSections = newSections.updateIn<MetricsModel>([sectionIndex, 'metrics', uid], metricsModel => {
      if (metricsModel) {
        const values = metricsModel.values.toJS<string>()
        const newValues = Array.from(new Set([...values, metricsModel.method])).filter((v: string) => v !== method)
        return (FactoryMetricItemModel as unknown as Factory).of({
          values: newValues,
          uid,
          method
        }) as MetricsModel
      } else {
        return (FactoryMetricItemModel as unknown as Factory).of({ uid, method }) as MetricsModel
      }
    })
  }

  return updateCurrentBlock(state, { sections: newSections })
}

export type GroupAddPayload = {
  section: string
  group: string
  target: string
}

export const groupAdd = (state: InsightsDashboardPoolState, { section, group, target }: GroupAddPayload) => {
  const block = getCurrentBlock(state)
  const groups = state.currentSchema?.get<GroupListModel>('groups') || null

  const dataMetrics = state.currentData?.dataMetrics || {}
  const attributes = state.currentData?.attributes || {}
  const sections = block?.getSections()
  const getMetrics = metricsFactory({ attributes, dataMetrics, sections })

  const existingGroup = groups?.find(g => g.name === group)
  if (!existingGroup) {
    return state
  }
  const groupAttributes = existingGroup.items.toJS()
  const metricsArray = groupAttributes.map(attribute => getMetrics(attribute, section))
  const newSections = addAttributesToSection({ sections, attributes: groupAttributes, section, target, metricsArray })

  return updateCurrentBlock(state, { sections: newSections })
}
